import { useState, useMemo } from "react";
import {
  Box,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Stack,
  Typography,
  Alert,
  Divider,
  Card,
  CardContent,
  Autocomplete,
  Grid,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import HistoryIcon from "@mui/icons-material/History";
import { ExportDataButton } from "./components/ExportDataDialog";
import {
  getDefaultExportDateFrom,
  getDefaultExportDateTo,
  isDateInRange,
} from "./utils/exportRetailData";
import { obtenerFechaHoyISO } from "../../utils/horaLocal";
export default function Compras({ store }) {
  const [proveedor, setProveedor] = useState("");
  const [fechaRegistro, setFechaRegistro] = useState(() => obtenerFechaHoyISO());
  const [items, setItems] = useState([]);
  const [productoSeleccionado, setProductoSeleccionado] = useState(null);
  const [cantidad, setCantidad] = useState("");
  const [precioCompra, setPrecioCompra] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [vistaHistorial, setVistaHistorial] = useState(false);
  const [fechaDesde, setFechaDesde] = useState(() => getDefaultExportDateFrom());
  const [fechaHasta, setFechaHasta] = useState(() => getDefaultExportDateTo());

  const totalCompra = useMemo(
    () => items.reduce((acc, i) => acc + i.cantidad * i.precio_compra, 0),
    [items]
  );

  const costoPromedioSeleccionado = useMemo(() => {
    if (!productoSeleccionado) return 0;
    if (typeof store.obtenerCostoPromedioProducto !== "function") {
      return parseFloat(productoSeleccionado.precio_compra) || 0;
    }
    return store.obtenerCostoPromedioProducto(productoSeleccionado.id) || 0;
  }, [productoSeleccionado, store]);

  const agregarItem = () => {
    if (!productoSeleccionado) return;
    const cant = parseInt(cantidad);
    const precio = parseFloat(precioCompra);
    if (!cant || cant <= 0) return;
    if (!precio || precio <= 0) return;

    const existe = items.find(
      (i) => i.producto_id === productoSeleccionado.id
    );
    if (existe) {
      setItems((prev) =>
        prev.map((i) =>
          i.producto_id === productoSeleccionado.id
            ? { ...i, cantidad: i.cantidad + cant, precio_compra: precio }
            : i
        )
      );
    } else {
      setItems((prev) => [
        ...prev,
        {
          producto_id: productoSeleccionado.id,
          nombre: productoSeleccionado.nombre,
          codigo_barras: productoSeleccionado.codigo_barras,
          cantidad: cant,
          precio_compra: precio,
        },
      ]);
    }

    setProductoSeleccionado(null);
    setCantidad("");
    setPrecioCompra("");
  };

  const eliminarItem = (productoId) => {
    setItems((prev) => prev.filter((i) => i.producto_id !== productoId));
  };

  const handleGuardar = async () => {
    if (!proveedor.trim()) {
      setFeedback({ tipo: "error", msg: "Ingresa el nombre del proveedor" });
      return;
    }
    if (items.length === 0) {
      setFeedback({
        tipo: "error",
        msg: "Agrega al menos un producto a la compra",
      });
      return;
    }

    const result = await store.registrarCompra(proveedor, items, {
      fecha: fechaRegistro,
    });

    if (result?.ok) {
      setFeedback({ tipo: "success", msg: "Compra registrada. Stock actualizado." });
      setProveedor("");
      setItems([]);
      setFechaRegistro(obtenerFechaHoyISO());
    } else if (result?.queued) {
      setFeedback({
        tipo: "success",
        msg: "Sin conexión con servidor. Compra guardada localmente y en cola; stock actualizado offline.",
      });
      setProveedor("");
      setItems([]);
      setFechaRegistro(obtenerFechaHoyISO());
    } else {
      setFeedback({ tipo: "error", msg: "Error al registrar la compra" });
    }

    setTimeout(() => setFeedback(null), 4000);
  };

  // Historial de compras (incluye fallback inferido desde detalleCompras si faltan cabeceras)
  const comprasFiltradas = useMemo(() => {
    const comprasMap = new Map((store.compras || []).map((c) => [c.id, { ...c, _inferred: false }]));

    (store.detalleCompras || []).forEach((d) => {
      const compraId = String(d.compra_id || "").trim();
      if (!compraId || comprasMap.has(compraId)) return;

      comprasMap.set(compraId, {
        id: compraId,
        proveedor: "(sin proveedor)",
        total: 0,
        fecha: null,
        _inferred: true,
      });
    });

    for (const compra of comprasMap.values()) {
      const detalles = (store.detalleCompras || []).filter((d) => d.compra_id === compra.id);
      if (compra._inferred || compra.total === undefined || compra.total === null) {
        compra.total = detalles.reduce(
          (acc, d) => acc + (parseInt(d.cantidad, 10) || 0) * (parseFloat(d.precio_compra) || 0),
          0
        );
      }
    }

    let compras = [...comprasMap.values()].sort((a, b) => {
      const ta = a?.fecha ? new Date(a.fecha).getTime() : 0;
      const tb = b?.fecha ? new Date(b.fecha).getTime() : 0;
      return tb - ta;
    });

    if (fechaDesde || fechaHasta) {
      compras = compras.filter((c) => isDateInRange(c.fecha, fechaDesde, fechaHasta));
    }

    return compras;
  }, [store.compras, store.detalleCompras, fechaDesde, fechaHasta]);

  const totalComprasRegistradas = useMemo(() => {
    const ids = new Set();
    (store.compras || []).forEach((c) => ids.add(c.id));
    (store.detalleCompras || []).forEach((d) => {
      const compraId = String(d.compra_id || "").trim();
      if (compraId) ids.add(compraId);
    });
    return ids.size;
  }, [store.compras, store.detalleCompras]);

  if (vistaHistorial) {
    return (
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight={700}>
            Historial de Compras ({comprasFiltradas.length})
          </Typography>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <TextField
            type="date"
            value={fechaDesde}
            onChange={(e) => setFechaDesde(e.target.value)}
            size="small"
            sx={{ maxWidth: 160 }}
            InputLabelProps={{ shrink: true }}
            label="Desde"
          />
          <TextField
            type="date"
            value={fechaHasta}
            onChange={(e) => setFechaHasta(e.target.value)}
            size="small"
            sx={{ maxWidth: 160 }}
            InputLabelProps={{ shrink: true }}
            label="Hasta"
          />
          <ExportDataButton
            dataset="compras"
            store={store}
            compras={comprasFiltradas}
            label="Exportar"
          />
          <Button onClick={() => setVistaHistorial(false)} variant="outlined">
            ← Nueva Compra
          </Button>
        </Stack>
        </Stack>

        {comprasFiltradas.length === 0 ? (
          <Typography color="text.secondary" textAlign="center" sx={{ py: 4 }}>
            No hay compras en el rango seleccionado
          </Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ "& th": { fontWeight: 700 } }}>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Proveedor</TableCell>
                  <TableCell>Productos</TableCell>
                  <TableCell align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {comprasFiltradas.map((c) => {
                  const detalles = store.detalleCompras.filter(
                    (d) => d.compra_id === c.id
                  );
                  return (
                    <TableRow key={c.id} hover>
                      <TableCell>
                        {c.fecha
                          ? new Date(c.fecha).toLocaleDateString("es", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : "Sin fecha"}
                      </TableCell>
                      <TableCell>{c.proveedor}</TableCell>
                      <TableCell>
                        {detalles
                          .map((d) => `${d.nombre} (x${d.cantidad})`)
                          .join(", ")}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {store.config.moneda} {c.total.toFixed(2)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6" fontWeight={700}>
          Registrar Compra
        </Typography>
        <Button
          startIcon={<HistoryIcon />}
          onClick={() => setVistaHistorial(true)}
          variant="outlined"
          size="small"
        >
          Historial ({totalComprasRegistradas})
        </Button>
      </Stack>

      {feedback && (
        <Alert severity={feedback.tipo} sx={{ mb: 2 }}>
          {feedback.msg}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={8}>
          <TextField
            label="Proveedor"
            value={proveedor}
            onChange={(e) => setProveedor(e.target.value)}
            fullWidth
            size="small"
            placeholder="Nombre del proveedor..."
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            type="date"
            label="Fecha de la compra"
            value={fechaRegistro}
            onChange={(e) => setFechaRegistro(e.target.value)}
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
            helperText="Cambia la fecha si registras una compra olvidada"
          />
        </Grid>
      </Grid>

      {/* Agregar productos */}
      <Card variant="outlined" sx={{ mb: 3, p: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} mb={2}>
          Agregar productos a la compra
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <Autocomplete
              value={productoSeleccionado}
              onChange={(_, v) => {
                setProductoSeleccionado(v);
                if (v) {
                  const avg =
                    typeof store.obtenerCostoPromedioProducto === "function"
                      ? store.obtenerCostoPromedioProducto(v.id)
                      : parseFloat(v.precio_compra) || 0;
                  setPrecioCompra(String(avg || 0));
                }
              }}
              options={store.productos}
              getOptionLabel={(p) => `${p.nombre} (${p.codigo_barras})`}
              renderInput={(params) => (
                <TextField {...params} label="Producto" size="small" />
              )}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              label="Cantidad"
              value={cantidad}
              onChange={(e) => setCantidad(e.target.value)}
              type="number"
              size="small"
              fullWidth
              inputProps={{ min: 1 }}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              label="Precio compra"
              value={precioCompra}
              onChange={(e) => setPrecioCompra(e.target.value)}
              type="number"
              size="small"
              fullWidth
              helperText={
                productoSeleccionado
                  ? `Costo promedio actual: ${store.config.moneda} ${costoPromedioSeleccionado.toFixed(2)}`
                  : ""
              }
              inputProps={{ min: 0, step: 0.01 }}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={agregarItem}
              fullWidth
              disabled={!productoSeleccionado}
            >
              Agregar
            </Button>
          </Grid>
        </Grid>
      </Card>

      {/* Items de la compra */}
      {items.length > 0 && (
        <>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ "& th": { fontWeight: 700 } }}>
                  <TableCell>Producto</TableCell>
                  <TableCell>Código</TableCell>
                  <TableCell align="center">Cantidad</TableCell>
                  <TableCell align="right">P. Compra</TableCell>
                  <TableCell align="right">Subtotal</TableCell>
                  <TableCell align="center" width={60}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.producto_id} hover>
                    <TableCell>{item.nombre}</TableCell>
                    <TableCell>
                      <Typography fontFamily="monospace" fontSize={12}>
                        {item.codigo_barras}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">{item.cantidad}</TableCell>
                    <TableCell align="right">
                      {store.config.moneda}{" "}
                      {item.precio_compra.toFixed(2)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {store.config.moneda}{" "}
                      {(item.cantidad * item.precio_compra).toFixed(2)}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => eliminarItem(item.producto_id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Divider sx={{ my: 2 }} />

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6" fontWeight={700}>
              Total: {store.config.moneda} {totalCompra.toFixed(2)}
            </Typography>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleGuardar}
              size="large"
              sx={{ px: 4 }}
            >
              Registrar Compra
            </Button>
          </Stack>
        </>
      )}

      {items.length === 0 && (
        <Typography
          color="text.secondary"
          textAlign="center"
          sx={{ py: 3 }}
        >
          Selecciona productos para agregar a esta compra
        </Typography>
      )}
    </Box>
  );
}
